import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, StatusDot } from '@/components/atoms';
import { useWorkflows } from '@/hooks/useWorkflows';
import { useStarred } from '@/store/useStarred';
import type { WorkflowSummary } from '@/types/workflow';

import styles from './Workflows.module.css';

type StatusFilter = 'all' | 'active' | 'paused' | 'override' | 'starred';
type ViewMode = 'list' | 'grid';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'starred', label: 'Favoritos' },
  { value: 'active', label: 'Activos' },
  { value: 'paused', label: 'Pausados' },
  { value: 'override', label: 'Overrides' },
];

const VIEW_KEY = 'wiredesk-workflows-view';
function loadView(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

export function Workflows() {
  const { data, isLoading, error, refetch } = useWorkflows();
  const starredIds = useStarred((s) => s.ids);
  const toggleStar = useStarred((s) => s.toggle);
  const starredSet = useMemo(() => new Set(starredIds), [starredIds]);

  const [search, setSearch] = useState('');
  // Default to "starred" view if user has any, else "all"
  const [status, setStatus] = useState<StatusFilter>(() =>
    starredIds.length > 0 ? 'starred' : 'all',
  );
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(loadView);

  // Promote first-time starring: if user just added their first star,
  // bump the filter to "starred" so they see their selection.
  useEffect(() => {
    if (starredIds.length === 1 && status === 'all') {
      setStatus('starred');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starredIds.length]);

  const allTags = useMemo(() => {
    if (!data) return [];
    const counts = new Map<string, number>();
    data.forEach((w) => {
      w.tags.forEach((t) => counts.set(t, (counts.get(t) ?? 0) + 1));
    });
    return Array.from(counts.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([tag, count]) => ({ tag, count }));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    return data.filter((w) => {
      if (q && !w.name.toLowerCase().includes(q)) return false;
      if (status === 'active' && !w.active) return false;
      if (status === 'paused' && w.active) return false;
      if (status === 'override' && !w.is_override) return false;
      if (status === 'starred' && !starredSet.has(w.id)) return false;
      if (tagFilter && !w.tags.includes(tagFilter)) return false;
      return true;
    });
  }, [data, search, status, tagFilter, starredSet]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, active: 0, paused: 0, overrides: 0, starred: 0 };
    return {
      total: data.length,
      active: data.filter((w) => w.active).length,
      paused: data.filter((w) => !w.active).length,
      overrides: data.filter((w) => w.is_override).length,
      starred: data.filter((w) => starredSet.has(w.id)).length,
    };
  }, [data, starredSet]);

  const setViewPersisted = (v: ViewMode) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // ignore
    }
  };

  const hasFilters = Boolean(search) || status !== 'all' || tagFilter !== null;

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>Workflows</h1>
        <Badge tone="info" size="sm">
          N8N
        </Badge>
        {data && (
          <span className={styles.totalBadge}>
            {filtered.length} / {stats.total}
          </span>
        )}
        <div className={styles.viewToggle} role="group">
          <button
            className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`}
            onClick={() => setViewPersisted('grid')}
            aria-pressed={view === 'grid'}
          >
            Cards
          </button>
          <button
            className={`${styles.viewBtn} ${view === 'list' ? styles.viewBtnActive : ''}`}
            onClick={() => setViewPersisted('list')}
            aria-pressed={view === 'list'}
          >
            Lista
          </button>
        </div>
      </header>

      <div className={styles.toolbar}>
        <input
          type="search"
          placeholder="Buscar workflow por nome…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchInput}
        />
        <div className={styles.statusFilters} role="group">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`${styles.chipFilter} ${status === f.value ? styles.chipFilterActive : ''}`}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
              {f.value === 'active' && stats.active > 0 && (
                <span className={styles.chipCount}>{stats.active}</span>
              )}
              {f.value === 'paused' && stats.paused > 0 && (
                <span className={styles.chipCount}>{stats.paused}</span>
              )}
              {f.value === 'override' && stats.overrides > 0 && (
                <span className={styles.chipCount}>{stats.overrides}</span>
              )}
              {f.value === 'starred' && stats.starred > 0 && (
                <span className={styles.chipCount}>{stats.starred}</span>
              )}
            </button>
          ))}
        </div>
        {hasFilters && (
          <button
            className={styles.clearBtn}
            onClick={() => {
              setSearch('');
              setStatus('all');
              setTagFilter(null);
            }}
          >
            Limpar
          </button>
        )}
      </div>

      {allTags.length > 0 && (
        <div className={styles.tagsBar}>
          <span className={styles.tagsLabel}>Tags:</span>
          {allTags.slice(0, 20).map(({ tag, count }) => (
            <button
              key={tag}
              className={`${styles.tagChip} ${tagFilter === tag ? styles.tagChipActive : ''}`}
              onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
            >
              {tag}
              <span className={styles.tagCount}>{count}</span>
            </button>
          ))}
        </div>
      )}

      {isLoading && <p className={styles.muted}>A carregar…</p>}
      {error && (
        <div className={styles.errorBanner}>
          Falha ao carregar workflows: {String(error)}.{' '}
          <button className={styles.linkBtn} onClick={() => refetch()}>
            Repetir
          </button>
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className={styles.empty}>Nenhum workflow com estes filtros.</div>
      )}

      {filtered.length > 0 && view === 'list' && (
        <ul className={styles.list}>
          {filtered.map((w) => (
            <WorkflowRow
              key={w.id}
              workflow={w}
              isStarred={starredSet.has(w.id)}
              onToggleStar={() => toggleStar(w.id)}
            />
          ))}
        </ul>
      )}

      {filtered.length > 0 && view === 'grid' && (
        <ul className={styles.grid}>
          {filtered.map((w) => (
            <WorkflowCard
              key={w.id}
              workflow={w}
              isStarred={starredSet.has(w.id)}
              onToggleStar={() => toggleStar(w.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface RowProps {
  workflow: WorkflowSummary;
  isStarred: boolean;
  onToggleStar: () => void;
}

function WorkflowRow({ workflow: w, isStarred, onToggleStar }: RowProps) {
  return (
    <li className={styles.row}>
      <button
        className={`${styles.starBtn} ${isStarred ? styles.starBtnActive : ''}`}
        onClick={onToggleStar}
        aria-label={isStarred ? 'Remover favorito' : 'Adicionar favorito'}
        aria-pressed={isStarred}
        title={isStarred ? 'Remover favorito' : 'Adicionar favorito'}
      >
        {isStarred ? 'Favoritado' : 'Favoritar'}
      </button>
      <StatusDot state={w.active ? 'ok' : 'off'} />
      <div className={styles.body}>
        <div className={styles.rowTitle}>
          <Link to={`/workflows/${w.id}`} className={styles.rowLink}>
            {w.name}
          </Link>
          {w.is_override && (
            <Badge tone="warn" size="xs">
              override
            </Badge>
          )}
          {!w.active && (
            <Badge tone="neutral" size="xs">
              pausado
            </Badge>
          )}
          {w.tags.map((t) => (
            <Badge key={t} tone="neutral" size="xs">
              {t}
            </Badge>
          ))}
        </div>
        <div className={styles.rowMeta}>
          <span className={styles.metaItem}>id: {w.id}</span>
          {w.trigger_type && (
            <span className={styles.metaItem}>trigger: {w.trigger_type}</span>
          )}
          {w.updated_at && (
            <span className={styles.metaItem}>
              actualizado:{' '}
              {new Date(w.updated_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function WorkflowCard({ workflow: w, isStarred, onToggleStar }: RowProps) {
  return (
    <li className={styles.gridCard}>
      <header className={styles.cardHead}>
        <button
          className={`${styles.starBtn} ${isStarred ? styles.starBtnActive : ''}`}
          onClick={onToggleStar}
          aria-label={isStarred ? 'Remover favorito' : 'Adicionar favorito'}
          aria-pressed={isStarred}
          title={isStarred ? 'Remover favorito' : 'Adicionar favorito'}
        >
          {isStarred ? 'Fav' : '+ Fav'}
        </button>
        <StatusDot state={w.active ? 'ok' : 'off'} />
        <Link to={`/workflows/${w.id}`} className={styles.cardLink}>
          {w.name}
        </Link>
      </header>
      <div className={styles.cardMeta}>
        {w.is_override && (
          <Badge tone="warn" size="xs">
            override
          </Badge>
        )}
        {!w.active && (
          <Badge tone="neutral" size="xs">
            pausado
          </Badge>
        )}
        {w.trigger_type && (
          <Badge tone="info" size="xs">
            {w.trigger_type}
          </Badge>
        )}
      </div>
      <div className={styles.cardTags}>
        {w.tags.slice(0, 4).map((t) => (
          <span key={t} className={styles.cardTag}>
            {t}
          </span>
        ))}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.metaItem}>{w.id.slice(0, 12)}</span>
        {w.updated_at && (
          <span className={styles.metaItem}>
            {new Date(w.updated_at).toLocaleDateString('pt-BR')}
          </span>
        )}
      </div>
    </li>
  );
}
